/**
 * LoadingSpinner
 * Reusable spinner used across admin and player pages.
 *
 * Props:
 *   fullScreen – centres the spinner in the full viewport (default: false)
 *   message    – optional label shown below the spinner
 *   dark       – use dark background (default: true for fullScreen, false inline)
 */

import React from 'react';

interface LoadingSpinnerProps {
  fullScreen?: boolean;
  message?: string;
  dark?: boolean;
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  fullScreen = false,
  message = 'Loading...',
  dark,
}) => {
  const useDark = dark !== undefined ? dark : fullScreen;

  const spinner = (
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin" />
      {message && (
        <p className={`text-sm font-medium ${useDark ? 'text-gray-300' : 'text-gray-600'}`}>
          {message}
        </p>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center">
        {spinner}
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center py-16">
      {spinner}
    </div>
  );
};

export default LoadingSpinner;
